/*
  This is the config for the questions in partner form. There are two objects here:

  partnerFormQuestionsConfig -> List of all the questions. Each question has a unique key which is linked to its respective answer from API schema
  {
    <category>: [{
      <subcategory>: [<list of questions>]
    }]
  }

  partnerFormDependencyConfig -> Dependency relation between the questions. It is a flat structure.

  {
    key: <parent question key>
    dependencies: [{
      <option value on which child question is dependant on>: [{
        key: <child question key>
        isDirectlyDependant: boolean // this is to identify whether indentaion is required or not
      }]
    }]
  }
*/

import React from 'react';
import { checkForMaxRetentionValue, checkForNonEmptyValue, checkForNonNegativeNumber, checkForNonSelectedValue, checkForValidEmail, checkRequiredValidLink, checkValidLink } from '../components/utils/questionValidations';
import { transformValueForDataRetention } from '../components/utils/transformDefaultValue';
import { HELPER_MESSAGE_TYPES, QuestionTypes } from '../types/PartnerForm.types';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const dataStorageSubcategory = {
  heading: 'Data Storage',
  questions: [{
    key: 'dataAccessAndStorage.appStoresEUDOutsideAtlassian',
    label: {
      text: 'Does your app store End-User Data outside of Atlassian products and services (excluding process/storage of End-User Data in logs)?'
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: '“End-User Data” is any data, content or information of an end user that is accessed, collected or otherwise processed by you or your app in connection with use of the Atlassian Marketplace.',
      type: HELPER_MESSAGE_TYPES.TEXT
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'dataAccessAndStorage.typesOfDataStored',
    label: {
      text: 'Please list the End-User Data types that your app stores'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    helperMessage: {
      text: 'For example: Email address, Device ID, IP address, Content posted, received or shared in the app by end-users.',
      type: HELPER_MESSAGE_TYPES.TEXT
    },
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'dataAccessAndStorage.appProcessEUDOutsideAtlassian',
    label: {
      text: 'Does your app process End-User Data outside of Atlassian products and services or outside of the end-user’s browser (excluding process/storage of End-User Data in logs)?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'dataAccessAndStorage.isSameDataProcessedAndStored',
    label: {
      text: ''
    },
    options: [{
      label: {
        text: 'End-User Data types processed and stored are the same.'
      },
      value: true
    }],
    type: QuestionTypes.TYPES_PROCESSED_AND_STORED_CHECK
  }, {
    key: 'dataAccessAndStorage.typesOfDataAccessed',
    label: {
      text: 'Please list the End-User Data types that your app processes'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    helperMessage: {
      text: 'For example: Email address, Device ID, IP address, Content posted, received or shared in the app by end-users.',
      type: HELPER_MESSAGE_TYPES.TEXT
    },
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'logDetails.logEndUserData',
    label: {
      text: 'Does your app log End-User Data?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'logDetails.logProcessAndOrStoreEUDOutsideAtlassian',
    label: {
      text: 'Does your app process and/or store End-User Data in logs outside of Atlassian products and services?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'dataRetention.isCustomRetentionPeriodAllowed',
    label: {
      text: 'Does your app allow customers to request a custom End-User Data retention period?'
    },
    isRequired: true,
    validate: checkForNonSelectedValue,
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }]
};
const dataEgressSubcategory = {
  heading: 'Data Egress',
  questions: [{
    key: 'thirdPartyInformation.isEndUserDataShared',
    isRequired: true,
    validate: checkForNonSelectedValue,
    label: {
      text: 'Does your app share End-User Data with any third party entities (e.g. sub-processors)?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'thirdPartyInformation.thirdPartyDetails',
    label: {
      text: "Please provide the following information for each third-party, or provide a link to your sub-processor list. For reference,learn more about Atlassian's sub-processor list.",
      href: 'https://www.atlassian.com/legal/sub-processors',
      linkText: "learn more about Atlassian's sub-processor list."
    },
    type: QuestionTypes.THIRD_PARTY_ENTITY_QUESTION,
    isRequired: true,
    validate: checkForNonEmptyValue,
    options: [{
      label: {
        text: 'Third party entity'
      },
      value: true,
      buttonText: 'Add another entity',
      textgroup: [{
        key: 'thirdPartyInformation.thirdPartyDetails.name',
        placeholder: 'Name of third party',
        type: QuestionTypes.TEXTFIELD,
        validate: checkForNonEmptyValue
      }, {
        key: 'thirdPartyInformation.thirdPartyDetails.link',
        placeholder: 'Domain in https://www.example.com format.',
        type: QuestionTypes.TEXTFIELD,
        validate: checkRequiredValidLink
      }, {
        key: 'thirdPartyInformation.thirdPartyDetails.thirdPartyCountries',
        placeholder: 'Countries where third party stores and/or processes End-User Data',
        type: QuestionTypes.COUNTRY_SELECT,
        validate: checkForNonEmptyValue
      }, {
        key: 'thirdPartyInformation.thirdPartyDetails.purpose',
        placeholder: 'Purpose of sharing End-User Data with third party (e.g. cloud hosting)',
        type: QuestionTypes.TEXTFIELD,
        validate: checkForNonEmptyValue
      }]
    }, {
      label: {
        text: 'Link to sub-processor list'
      },
      value: false,
      textfield: {
        key: 'thirdPartyInformation.dataSubProcessors',
        placeholder: 'For e.g. https://www.example.com',
        type: QuestionTypes.TEXTFIELD,
        label: {
          text: ''
        },
        validate: checkRequiredValidLink
      }
    }]
  }, {
    key: 'logDetails.logEUDShareWithThirdParty',
    label: {
      text: 'Does your app share logs that include End-User Data with any third-party entities?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'logDetails.logsIntegralForAppFunctionality',
    label: {
      text: 'Is sharing of logs that include End-User Data with any third party entities integral for app functionality?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }]
};
const dataResidencySubcategory = {
  heading: 'Data Residency',
  questions: [{
    key: 'dataResidency.isDataResidencySupported',
    type: QuestionTypes.RADIO,
    label: {
      text: 'Does your app support data residency options? If yes, please list the locations where in-scope End-User Data is stored. For example: EU, US'
    },
    helperMessage: {
      text: 'Learn more about Data residency',
      type: HELPER_MESSAGE_TYPES.TOOLTIP,
      content: [{
        type: 'text',
        text: 'Data residency allows you to control where in-scope End User Data is hosted. It allows you to choose whether in-scope End-User Data is globally distributed or in a defined geographic location.'
      }]
    },
    options: [{
      label: {
        text: 'Yes.'
      },
      value: 'SUPPORTS_DATA_RESIDENCY'
    }, {
      label: {
        text: 'No. App does not support data residency options.'
      },
      value: 'APP_DOES_NOT_SUPPORT_DR'
    }, {
      label: {
        text: "No. App stores End-User Data exclusively within Atlassian products and services, which don't support data residency options yet due to Atlassian’s current roadmap.",
        href: 'https://developer.atlassian.com/platform/forge/',
        linkText: 'Forge'
      },
      value: 'STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED'
    }, {
      label: {
        text: 'Not applicable. App does not store End-User Data.'
      },
      value: 'NOT_APPLICABLE'
    }]
  }, {
    key: 'dataResidency.isInScopeDataStoredOutsideAtlassian',
    label: {
      text: 'Does your app store any in-scope EUD independently outside of Atlassian products and services?'
    },
    type: QuestionTypes.RADIO,
    isRequired: true,
    validate: checkForNonSelectedValue,
    options: [{
      label: {
        text: 'Yes.'
      },
      value: 'STORED_EXTERNAL_TO_ATLASSIAN'
    }, {
      label: {
        text: 'No, any in-scope EUD is stored exclusively within Atlassian products and services.'
      },
      value: 'STORED_IN_ATLASSIAN_AND_DR_SUPPORTED'
    }]
  }, {
    key: 'dataResidency.countriesWhereEndUserDataStored',
    label: {
      text: 'List the locations where in-scope End-User Data is stored.'
    },
    placeholder: 'Select locations',
    type: QuestionTypes.COUNTRY_SELECT,
    isRequired: true,
    validate: checkForNonEmptyValue,
    helperMessage: {
      text: 'Learn more',
      type: HELPER_MESSAGE_TYPES.TOOLTIP,
      content: [{
        type: 'text',
        text: 'To support EU, please select the relevant AWS region, for example, Ireland and/or Germany'
      }]
    }
  }, {
    key: 'dataResidency.inScopeDataTypes',
    label: {
      text: "Please list the End-User Data that is in-scope for data residency. For example, here's Atlassian’s list of in-scope product data that is supported by data residency.",
      href: 'https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/',
      linkText: 'Atlassian’s list of in-scope product data that is supported by data residency.'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue,
    buttonText: 'Add more',
    helperMessage: {
      text: 'Learn more about in-scope End-User Data',
      type: HELPER_MESSAGE_TYPES.TOOLTIP,
      content: [{
        type: 'text',
        text: 'In-scope End-User Data is data that can be pinned to a particular region, in accordance with an app’s data residency options.'
      }]
    }
  }, {
    key: 'dataResidency.outOfScopeDataTypes',
    label: {
      text: 'Please list the End-User Data that is out-of-scope for data residency.'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue,
    buttonText: 'Add more'
  }, {
    key: 'dataResidency.realmMigrationSupported',
    label: {
      text: 'Does your app support migration of in-scope End User Data between your data residency supported locations?'
    },
    isRequired: true,
    validate: checkForNonSelectedValue,
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'dataRetention.isDataRetentionSupported',
    isRequired: true,
    validate: checkForNonSelectedValue,
    label: {
      text: 'Does your app store End-User Data after a customer uninstalls your app?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'dataRetention.isRetentionDurationIndefinite',
    label: {
      text: ''
    },
    options: [{
      label: {
        text: 'End-User Data stored indefinitely after app uninstallation'
      },
      value: false
    }],
    type: QuestionTypes.DATA_RETENTION_INDEFINITE
  }, {
    key: 'dataRetention.retentionDurationInDays',
    label: {
      text: 'What is the minimum and maximum data storage period for End-User Data after a customer uninstalls your app?'
    },
    valueTransformer: transformValueForDataRetention,
    isRequired: true,
    type: QuestionTypes.DATA_RETENTION_QUESTION,
    textgroup: [{
      placeholder: 'For e.g. 30',
      helperMessage: {
        text: 'Minimum storage period must be entered in days.',
        type: HELPER_MESSAGE_TYPES.TEXT
      },
      key: 'dataRetention.retentionDurationInDays.min',
      validate: checkForNonNegativeNumber
    }, {
      placeholder: 'For e.g. 90',
      helperMessage: {
        text: 'Maximum storage period must be entered in days.',
        type: HELPER_MESSAGE_TYPES.TEXT
      },
      key: 'dataRetention.retentionDurationInDays.max',
      validate: checkForMaxRetentionValue
    }]
  }]
};
const securityAndComplianceCategory = {
  questions: [{
    type: QuestionTypes.IN_SOURCED,
    label: {
      text: 'Integration permissions with Atlassian products'
    },
    defaultValue: 'Data fetched from backend',
    key: 'security.permission'
  }, {
    type: QuestionTypes.IN_SOURCED,
    label: {
      text: 'Marketplace Security Bug Bounty Program participant'
    },
    defaultValue: 'Data fetched from backend',
    key: 'security.bugbounty',
    helperMessage: {
      text: 'Learn more about this program',
      href: 'https://developer.atlassian.com/platform/marketplace/marketplace-security-bug-bounty-program/',
      type: HELPER_MESSAGE_TYPES.LINK
    }
  }, {
    key: 'security.hasCompliantCertifications',
    label: {
      text: 'Does your app have any compliance certifications?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'security.compliantCertifications',
    label: {
      text: 'Please add the certifications'
    },
    type: QuestionTypes.REPEATABLE_CREATABLE_SELECT,
    buttonText: 'Add another certification',
    placeholder: 'For e.g. SOC2, ISO27K, HIPAA',
    isRequired: true,
    validate: checkForNonEmptyValue,
    options: [{
      label: 'SOC2',
      value: 'SOC2'
    }, {
      label: 'ISO27K',
      value: 'ISO27K'
    }, {
      label: 'HIPAA',
      value: 'HIPAA'
    }, {
      label: 'FedRamp',
      value: 'FedRamp'
    }, {
      label: 'Other',
      value: 'Other'
    }],
    otherInputBoxLabel: {
      text: 'Please specify the certifications under `Other`'
    },
    otherInputBoxPlaceholder: 'Enter additional certification',
    otherInputBoxButtonText: 'Add More'
  }, {
    key: 'security.securityContact',
    label: {
      text: 'Which email address can customers use to contact you for app security issues?'
    },
    type: QuestionTypes.TEXTFIELD,
    placeholder: 'Email address',
    isRequired: true,
    validate: checkForValidEmail
  }, {
    key: 'security.caiq.isCAIQCompleted',
    label: {
      text: 'Have you completed a CAIQ Lite Questionnaire that covers this app?'
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: 'Learn more about CAIQ Lite',
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://cloudsecurityalliance.org/star/caiq-lite/'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'security.caiq.CAIQLiteLink',
    label: {
      text: 'Please link your CAIQ Lite Questionnaire responses'
    },
    type: QuestionTypes.TEXTFIELD,
    isRequired: true,
    validate: checkRequiredValidLink,
    placeholder: 'For e.g. https://www.example.com'
  }, {
    key: 'security.publicSecurityPoliciesLink',
    label: {
      text: 'Please provide your security policy:'
    },
    type: QuestionTypes.TEXTFIELD,
    placeholder: 'For e.g. https://www.example.com',
    validate: checkValidLink
  }, {
    key: 'security.isDiskEncryptionSupported',
    label: {
      text: 'Does your app use full disk encryption at-rest for End-User Data stored outside of Atlassian or the users’s browser?'
    },
    type: QuestionTypes.RADIO,
    isRequired: true,
    validate: checkForNonSelectedValue,
    helperMessage: {
      text: 'Learn more about security requirements for cloud applications',
      type: HELPER_MESSAGE_TYPES.TOOLTIP,
      content: [{
        type: 'text',
        text: 'Atlassian maintains a list of security requirements for cloud applications. This requirement is mandated by Atlassian to ensure security across the Marketplace.'
      }, {
        type: 'link',
        text: 'Read more about security requirements',
        href: 'https://developer.atlassian.com/platform/marketplace/security-requirements/'
      }]
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }]
};
const privacyCategory = {
  questions: [{
    key: 'privacy.policyLink',
    label: {
      text: 'Privacy policy as set out in Atlassian’s Marketplace Partner Agreement.',
      linkText: 'Marketplace Partner Agreement.',
      href: 'https://www.atlassian.com/licensing/marketplace/partneragreement'
    },
    type: QuestionTypes.IN_SOURCED,
    defaultValue: /*#__PURE__*/_jsxs("span", {
      children: ["Data sourced from your response to the ", /*#__PURE__*/_jsx("b", {
        children: "'data security and privacy statement'"
      }), " field submitted during app publish flow"]
    })
  }, {
    key: 'privacy.privacyEnhancingTechniques.arePrivacyEnhancingTechniquesSupported',
    label: {
      text: 'Does your app use any privacy enhancing technologies (PETs) to protect End-User Data?'
    },
    type: QuestionTypes.RADIO,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'privacy.privacyEnhancingTechniques.privacyEnhancingTechniquesSupported',
    label: {
      text: 'Please list any PETs used'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    placeholder: 'For e.g pseudonymization, anonymization',
    isRequired: true,
    validate: checkForNonEmptyValue,
    buttonText: 'Add another PET'
  }, {
    key: 'privacy.gdpr.dataController.isAppDataController',
    label: {
      text: "Is your company/organization a 'data controller' under the General Data Protection Regulation (GDPR) with reference to this app?"
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: "Learn more about 'data controllers' under the GDPR",
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/reform/rules-business-and-organisations/obligations/controller-processor/what-data-controller-or-data-processor_en'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: 'TRUE'
    }, {
      label: {
        text: 'No'
      },
      value: 'FALSE'
    }, {
      label: {
        text: 'Not applicable - Company/Organization is not subject to the GDPR.'
      },
      value: 'NOT_APPLICABLE'
    }]
  }, {
    key: 'privacy.gdpr.dataController.endUserDataTypes',
    label: {
      text: "Please specify the End-User Data with respect to which your app is a 'data controller.'"
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'privacy.gdpr.dataProcessor.isAppDataProcessor',
    label: {
      text: "Is your company/organization a 'data processor' under the General Data Protection Regulation (GDPR) with reference to this app?"
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: "Learn more about 'data processors' under the GDPR",
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/reform/rules-business-and-organisations/obligations/controller-processor/what-data-controller-or-data-processor_en'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: 'TRUE'
    }, {
      label: {
        text: 'No'
      },
      value: 'FALSE'
    }, {
      label: {
        text: 'Not applicable - Company/Organization is not subject to the GDPR.'
      },
      value: 'NOT_APPLICABLE'
    }]
  }, {
    key: 'privacy.gdpr.dataProcessor.endUserDataTypes',
    label: {
      text: "Please specify the End-User Data with respect to which your app is a 'data processor.'"
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'privacy.ccpa.business.isAppBusiness',
    label: {
      text: "Is your company/organization a 'business' under the California Consumer Privacy Act of 2018 (CCPA) with reference to this app?"
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: "Learn more about 'businesses' under the CCPA",
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://oag.ca.gov/privacy/ccpa'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: 'TRUE'
    }, {
      label: {
        text: 'No'
      },
      value: 'FALSE'
    }, {
      label: {
        text: 'Not applicable - Company/Organization is not subject to the CCPA.'
      },
      value: 'NOT_APPLICABLE'
    }]
  }, {
    key: 'privacy.ccpa.business.endUserDataTypes',
    label: {
      text: 'Please specify the End-User Data with respect to which your app is a “business.”'
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'privacy.ccpa.serviceProvider.isAppServiceProvider',
    label: {
      text: "Is your company/organization a 'service provider' under the California Consumer Privacy Act of 2018 (CCPA) with reference to this app?"
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: "Learn more about 'service providers' under the CCPA",
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://oag.ca.gov/privacy/ccpa'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: 'TRUE'
    }, {
      label: {
        text: 'No'
      },
      value: 'FALSE'
    }, {
      label: {
        text: 'Not applicable - Company/Organization is not subject to the CCPA.'
      },
      value: 'NOT_APPLICABLE'
    }]
  }, {
    key: 'privacy.ccpa.serviceProvider.endUserDataTypes',
    label: {
      text: "Please specify the End-User Data with respect to which your app is a 'service provider.'"
    },
    type: QuestionTypes.REPEATABLE_TEXTFIELD,
    buttonText: 'Add more',
    placeholder: 'Enter End-User Data type',
    isRequired: true,
    validate: checkForNonEmptyValue
  }, {
    key: 'privacy.dataProcessingAgreement.isDPASupported',
    label: {
      text: 'Does your app have a Data Processing Agreement (DPA) for customers?'
    },
    type: QuestionTypes.RADIO,
    helperMessage: {
      text: 'Learn more about DPAs',
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://gdpr.eu/what-is-data-processing-agreement/'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: 'TRUE'
    }, {
      label: {
        text: 'No'
      },
      value: 'FALSE'
    }]
  }, {
    key: 'privacy.dataProcessingAgreement.link',
    label: {
      text: 'Please share the DPA'
    },
    type: QuestionTypes.TEXTFIELD,
    placeholder: 'For e.g. https://www.example.com',
    isRequired: true,
    validate: checkRequiredValidLink
  }, {
    key: 'privacy.gdpr.dataTransfer.isEndUserDataTransferredOutsideEEA',
    label: {
      text: "Does your app transfer European Economic Area (EEA) residents's End-User Data outside of the EEA?"
    },
    type: QuestionTypes.RADIO,
    isRequired: true,
    validate: checkForNonSelectedValue,
    helperMessage: {
      text: 'Learn more about GDPR approved transfer mechanisms',
      type: HELPER_MESSAGE_TYPES.LINK,
      href: 'https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/rules-international-data-transfers_en'
    },
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'privacy.gdpr.dataTransfer.isTransferComplianceMechanismsAdhered',
    label: {
      text: 'Does your app have a General Data Protection Regulation (GDPR) approved transfer mechanism in place to govern those transfers?'
    },
    type: QuestionTypes.RADIO,
    isRequired: true,
    validate: checkForNonSelectedValue,
    options: [{
      label: {
        text: 'Yes'
      },
      value: true
    }, {
      label: {
        text: 'No'
      },
      value: false
    }]
  }, {
    key: 'privacy.gdpr.dataTransfer.transferComplianceMechanismsAdheredDetails',
    label: {
      text: 'Please provide the transfer mechanism used'
    },
    isRequired: true,
    validate: checkForNonEmptyValue,
    type: QuestionTypes.TEXTFIELD,
    placeholder: 'For e.g. Standard Contractual Clauses (SCCs)'
  }]
};
export const partnerFormQuestionsConfigWithDaReChanges = [{
  heading: 'Data storage and management',
  subcategory: [dataStorageSubcategory, dataEgressSubcategory, dataResidencySubcategory]
}, {
  heading: 'Security and Compliance',
  subcategory: [securityAndComplianceCategory]
}, {
  heading: 'Privacy',
  subcategory: [privacyCategory]
}];

// eslint-disable-next-line max-lines-per-function
export const partnerFormDependencyConfigWithDaReChanges = [{
  key: 'dataAccessAndStorage.appStoresEUDOutsideAtlassian',
  dependencies: [{
    true: [{
      key: 'dataAccessAndStorage.typesOfDataStored',
      level: 1
    }, {
      key: 'dataRetention.isCustomRetentionPeriodAllowed',
      level: 0
    }, {
      key: 'dataRetention.isDataRetentionSupported',
      level: 0
    }, {
      key: 'security.isDiskEncryptionSupported',
      level: 0
    }, {
      key: 'thirdPartyInformation.isEndUserDataShared',
      level: 0
    }, {
      key: 'privacy.privacyEnhancingTechniques.arePrivacyEnhancingTechniquesSupported',
      level: 0
    }, {
      key: 'privacy.gdpr.dataTransfer.isEndUserDataTransferredOutsideEEA',
      level: 0
    }]
  }]
}, {
  key: 'dataAccessAndStorage.appProcessEUDOutsideAtlassian',
  dependencies: [{
    true: [{
      key: 'dataAccessAndStorage.isSameDataProcessedAndStored',
      level: 1
    }, {
      key: 'privacy.privacyEnhancingTechniques.arePrivacyEnhancingTechniquesSupported',
      level: 0
    }, {
      key: 'privacy.gdpr.dataTransfer.isEndUserDataTransferredOutsideEEA',
      level: 0
    }]
  }]
}, {
  key: 'dataAccessAndStorage.isSameDataProcessedAndStored',
  dependencies: [{
    false: [{
      key: 'dataAccessAndStorage.typesOfDataAccessed',
      level: 1
    }]
  }]
}, {
  key: 'thirdPartyInformation.isEndUserDataShared',
  dependencies: [{
    true: [{
      key: 'thirdPartyInformation.thirdPartyDetails',
      level: 1
    }, {
      key: 'thirdPartyInformation.dataSubProcessors',
      level: 1
    }]
  }]
}, {
  key: 'dataResidency.isDataResidencySupported',
  dependencies: [{
    SUPPORTS_DATA_RESIDENCY: [{
      key: 'dataResidency.isInScopeDataStoredOutsideAtlassian',
      level: 1
    }]
  }]
}, {
  key: 'dataResidency.isInScopeDataStoredOutsideAtlassian',
  dependencies: [{
    STORED_IN_ATLASSIAN_AND_DR_SUPPORTED: [{
      key: 'dataResidency.inScopeDataTypes',
      level: 1
    }, {
      key: 'dataResidency.outOfScopeDataTypes',
      level: 1
    }]
  }, {
    STORED_EXTERNAL_TO_ATLASSIAN: [{
      key: 'dataResidency.countriesWhereEndUserDataStored',
      level: 1
    }, {
      key: 'dataResidency.inScopeDataTypes',
      level: 1
    }, {
      key: 'dataResidency.outOfScopeDataTypes',
      level: 1
    }, {
      key: 'dataRetention.isDataRetentionSupported',
      level: 0
    }, {
      key: 'dataResidency.realmMigrationSupported',
      level: 1
    }]
  }]
}, {
  key: 'dataRetention.isDataRetentionSupported',
  dependencies: [{
    true: [{
      key: 'dataRetention.isRetentionDurationIndefinite',
      level: 1
    }]
  }]
}, {
  key: 'dataRetention.isRetentionDurationIndefinite',
  dependencies: [{
    false: [{
      key: 'dataRetention.retentionDurationInDays',
      level: 1
    }, {
      key: 'dataRetention.retentionDurationInDays.min',
      level: 1
    }, {
      key: 'dataRetention.retentionDurationInDays.max',
      level: 1
    }],
    null: [{
      key: 'dataRetention.retentionDurationInDays',
      level: 1
    }, {
      key: 'dataRetention.retentionDurationInDays.min',
      level: 1
    }, {
      key: 'dataRetention.retentionDurationInDays.max',
      level: 1
    }]
  }]
}, {
  key: 'security.hasCompliantCertifications',
  dependencies: [{
    true: [{
      key: 'security.compliantCertifications',
      level: 1
    }]
  }]
}, {
  key: 'security.caiq.isCAIQCompleted',
  dependencies: [{
    true: [{
      key: 'security.caiq.CAIQLiteLink',
      level: 1
    }]
  }]
}, {
  key: 'privacy.privacyEnhancingTechniques.arePrivacyEnhancingTechniquesSupported',
  dependencies: [{
    true: [{
      key: 'privacy.privacyEnhancingTechniques.privacyEnhancingTechniquesSupported',
      level: 1
    }]
  }]
}, {
  key: 'privacy.gdpr.dataController.isAppDataController',
  dependencies: [{
    TRUE: [{
      key: 'privacy.gdpr.dataController.endUserDataTypes',
      level: 1
    }]
  }]
}, {
  key: 'privacy.gdpr.dataProcessor.isAppDataProcessor',
  dependencies: [{
    TRUE: [{
      key: 'privacy.gdpr.dataProcessor.endUserDataTypes',
      level: 1
    }, {
      key: 'privacy.dataProcessingAgreement.isDPASupported',
      level: 0
    }]
  }]
}, {
  key: 'privacy.ccpa.business.isAppBusiness',
  dependencies: [{
    TRUE: [{
      key: 'privacy.ccpa.business.endUserDataTypes',
      level: 1
    }]
  }]
}, {
  key: 'privacy.ccpa.serviceProvider.isAppServiceProvider',
  dependencies: [{
    TRUE: [{
      key: 'privacy.ccpa.serviceProvider.endUserDataTypes',
      level: 1
    }, {
      key: 'privacy.dataProcessingAgreement.isDPASupported',
      level: 0
    }]
  }]
}, {
  key: 'privacy.dataProcessingAgreement.isDPASupported',
  dependencies: [{
    TRUE: [{
      key: 'privacy.dataProcessingAgreement.link',
      level: 1
    }]
  }]
}, {
  key: 'privacy.gdpr.dataTransfer.isEndUserDataTransferredOutsideEEA',
  dependencies: [{
    true: [{
      key: 'privacy.gdpr.dataTransfer.isTransferComplianceMechanismsAdhered',
      level: 1
    }]
  }]
}, {
  key: 'privacy.gdpr.dataTransfer.isTransferComplianceMechanismsAdhered',
  dependencies: [{
    true: [{
      key: 'privacy.gdpr.dataTransfer.transferComplianceMechanismsAdheredDetails',
      level: 2
    }]
  }]
}];